import React, { useState } from 'react';

import { Alert } from '@codes/ui-kit/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';

import { cardQueryKeys, createCard } from '@codes/payments';
import { Button, Input, Label } from '@codes/ui-kit';
import { useWebCoreStore } from '@codes/web-core';

const COUNTRIES = [
    { value: 'US', label: 'United States' },
    { value: 'KR', label: '대한민국' },
    { value: 'JP', label: '일본' },
    // TODO: add more
];

interface CardRegistrationFormProps {
    onSuccess?: (createdCard: CardView) => void;
    currency?: string;
}

export const CardRegistrationForm = ({ onSuccess, currency = 'USD' }: CardRegistrationFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const queryClient = useQueryClient();

    const userEmail = useWebCoreStore(state => state.email);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        cardName: '',
        country: '',
        zipCode: '',
    });

    const isFormValid = () => {
        return (
            formData.email.trim() !== '' &&
            formData.cardName.trim() !== '' &&
            formData.country !== '' &&
            formData.zipCode.trim() !== ''
        );
    };

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCardChange = (event: any) => {
        if (event.error) {
            setError(event.error.message);
        } else {
            setError(null);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            setError('Stripe가 아직 로드되지 않았습니다.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                throw new Error('카드 정보를 찾을 수 없습니다.');
            }

            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: formData.cardName,
                    email: formData.email,
                    address: {
                        country: formData.country,
                        postal_code: formData.zipCode,
                    },
                },
            });

            if (stripeError) {
                throw new Error(stripeError.message);
            }

            if (!paymentMethod) {
                throw new Error('결제 수단 생성에 실패했습니다.');
            }

            console.log('Created Payment Method:', paymentMethod);

            const createdCard = await createCard({
                token: paymentMethod.id,
                type: 'stripe',
                email: userEmail,
            });

            console.log(createdCard);

            if (!createdCard) {
                // error
                return;
            }

            await queryClient.invalidateQueries(cardQueryKeys.invalidateList());
            setSuccess(true);

            if (onSuccess) {
                // TODO: isValid 수정해서 사용해도 되는지 서버에 문의 필요
                onSuccess({ ...createdCard, isValid: true });
            }
            const closeButton = document.querySelector('[data-dialog-close]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (err) {
            setError(err.message || '카드 등록 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <Label variant="form">Email</Label>
                <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="jane.diaz@example.com"
                    required
                />
            </div>

            <div>
                <Label variant="form">Card information</Label>
                <div className="px-5 py-4 border border-text-input-border rounded-lg">
                    <CardElement
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                        color: '#aab7c4',
                                    },
                                },
                                invalid: {
                                    color: '#9e2146',
                                },
                            },
                            hidePostalCode: true,
                        }}
                        onChange={handleCardChange}
                    />
                </div>
            </div>

            <div>
                <Label variant="form">Name on card</Label>
                <Input
                    type="text"
                    name="cardName"
                    value={formData.cardName}
                    onChange={handleInputChange}
                    placeholder="Jane Diaz"
                    required
                />
            </div>

            <div>
                <Label variant="form">Country or region</Label>
                <Select
                    value={formData.country}
                    onValueChange={value => setFormData(prev => ({ ...prev, country: value }))}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                        {COUNTRIES.map(country => (
                            <SelectItem key={country.value} value={country.value}>
                                {country.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {formData.country && (
                    <Input
                        className="mt-2"
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="ZIP code"
                        required
                    />
                )}
            </div>

            <Button size="lg" className="w-full" type="submit" disabled={loading || !isFormValid()}>
                {loading ? 'Processing...' : 'Register'}
            </Button>

            {error && <Alert variant="destructive">{error}</Alert>}

            {success && (
                <Alert className="bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100">
                    카드가 성공적으로 등록되었습니다!
                </Alert>
            )}
        </form>
    );
};
